/**
 * Display labels for the canonical sector enum — the public Raises directory &
 * profile mirror of `SECTOR_LABELS` in
 * `my-backend/src/services/activeRaises/normalization/sectorTaxonomy.ts`.
 *
 * The API serializes the canonical machine value (`solar`, `oil_gas_lng`, …);
 * the UI renders these human labels. Kept in lockstep with the backend table so
 * the directory facets, result rows, and profile chips all read identically.
 */
import type { CanonicalSector } from '@/types/raises';

/**
 * Canonical sector → display label. Mirrors the backend `SECTOR_LABELS` map
 * field-for-field (order preserved for stable facet rendering); the backend
 * parity test `sectorTaxonomyFrontendParity.test.ts` fails on any drift.
 */
export const SECTOR_LABELS: Record<CanonicalSector, string> = {
  solar: 'Solar',
  wind: 'Wind',
  hydro: 'Hydro',
  geothermal: 'Geothermal',
  nuclear: 'Nuclear',
  power_generation: 'Power Generation',
  transmission_grid: 'Transmission & Grid',
  energy_storage: 'Energy Storage',
  hydrogen_ammonia: 'Hydrogen & Ammonia',
  bioenergy_fuels: 'Bioenergy & Low-Carbon Fuels',
  carbon_management: 'Carbon Capture & Removal',
  oil_gas_lng: 'Oil, Gas & LNG',
  water_sanitation: 'Water & Sanitation',
  ports_marine: 'Ports & Marine',
  roads_highways: 'Roads & Highways',
  rail: 'Rail',
  airports: 'Airports',
  transport_other: 'Transport (Other)',
  digital_infrastructure: 'Digital Infrastructure',
  telecom_fiber: 'Telecom & Fiber',
  social_infrastructure: 'Social & Real Estate Infrastructure',
  mining: 'Mining',
  minerals_processing: 'Minerals Processing & Refining',
  agriculture: 'Agriculture',
  advanced_manufacturing: 'Advanced Manufacturing',
  defence_aerospace: 'Defence & Aerospace',
  other: 'Other',
};

/**
 * Maps a canonical sector value to its display label.
 *  - `null` / `undefined` → `'—'` (muted empty-cell placeholder).
 *  - Unknown value → the raw value verbatim (forward-compatible if the enum
 *    grows ahead of this map).
 */
export function sectorLabel(sector: string | null | undefined): string {
  if (sector === null || sector === undefined) return '—';
  return SECTOR_LABELS[sector as CanonicalSector] ?? sector;
}
